import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError, downloadFichesPdf } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ShieldPlus, Printer, Plus, Copy, CalendarDays, Package, Users, Layers,
  Sun, Moon, Boxes, Save, Loader2, Pencil, Trash2, FileText,
} from "lucide-react";
import { ProductsDialog, PROTECTION_ICONS } from "../components/incontinence/ProductsDialog";
import { StatsBudget } from "../components/incontinence/StatsBudget";

const fmtNum = (n) => Number(n || 0).toLocaleString("fr-BE", { maximumFractionDigits: 4 });
const iconUrlFor = (key) => PROTECTION_ICONS.find((i) => i.key === key)?.url || "";

function Kpi({ icon: Icon, label, value, sub, grad, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay }}
      className="relative overflow-hidden rounded-2xl card-3d p-5" style={{ background: grad }} data-testid={`inc-kpi-${label}`}>
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-xl" />
      <Icon className="h-6 w-6 text-white/90" />
      <div className="mt-3 text-3xl font-display font-extrabold text-white leading-none">{value}</div>
      <div className="text-sm text-white/85 mt-1 font-medium">{label}</div>
      {sub && <div className="text-xs text-white/70 mt-0.5">{sub}</div>}
    </motion.div>
  );
}

export default function Incontinence() {
  const { t } = useTranslation();
  const dayLabel = (d) => t(`enums.day.${d}`, { defaultValue: d });
  const [setup, setSetup] = useState({ products: [], units: [], residents: [], consignes: [] });
  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState("");
  const [meta, setMeta] = useState({ label: "" });
  const [needs, setNeeds] = useState({});
  const [slots, setSlots] = useState({});
  const [remainders, setRemainders] = useState({});
  const [remainderCartons, setRemainderCartons] = useState({});
  const [stockRem, setStockRem] = useState({});
  const [stockRemCarton, setStockRemCarton] = useState({});
  const [checks, setChecks] = useState({});
  const [activeUnit, setActiveUnit] = useState("");
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [copyMode, setCopyMode] = useState(false);
  const [wf, setWf] = useState({ start_date: "", end_date: "", label: "" });
  const loadedRef = useRef(false);
  const timerRef = useRef(null);

  const loadSetup = async () => { const { data } = await api.get("/inc/setup"); setSetup(data); if (data.units[0]) setActiveUnit((u) => u || data.units[0].id); };
  const loadWeeks = async () => { const { data } = await api.get("/inc/weeks"); setWeeks(data); return data; };

  const loadWeek = async (id) => {
    loadedRef.current = false;
    const { data } = await api.get(`/inc/weeks/${id}`);
    setMeta({ label: data.label, start_date: data.start_date, end_date: data.end_date });
    setNeeds(data.needs || {}); setSlots(data.slots || {}); setRemainders(data.remainders || {}); setRemainderCartons(data.remainder_cartons || {}); setStockRem(data.stock_remainders || {}); setStockRemCarton(data.stock_remainder_cartons || {}); setChecks(data.checks || {});
    setWeekId(id);
    setTimeout(() => { loadedRef.current = true; }, 50);
  };

  useEffect(() => {
    (async () => {
      await loadSetup();
      const w = await loadWeeks();
      if (w[0]) await loadWeek(w[0].id);
    })();
  }, []);

  const products = useMemo(() => setup.products.filter((p) => p.active !== false).sort((a, b) => a.display_order - b.display_order), [setup.products]);
  const jour = products.filter((p) => p.category === "JOUR");
  const nuit = products.filter((p) => p.category === "NUIT");
  const autres = products.filter((p) => p.category === "AUTRE");
  const units = [...setup.units].sort((a, b) => a.display_order - b.display_order);
  const residentsOf = (uid) => setup.residents.filter((r) => r.unit_id === uid && r.active !== false).sort((a, b) => a.display_order - b.display_order);

  const SLOT_DEFS = [
    { key: "7h", label: "7h", cat: "JOUR" },
    { key: "14h", label: "14h", cat: "JOUR" },
    { key: "19h", label: "19h", cat: "JOUR" },
    { key: "2h", label: "2h", cat: "NUIT" },
  ];

  // Besoins produits dérivés des créneaux horaires
  const derivedNeeds = useMemo(() => {
    const out = {};
    for (const rid in slots) {
      const r = slots[rid] || {};
      out[rid] = {};
      for (const k of Object.keys(r)) {
        const s = r[k];
        const q = Number(s?.qty || 0);
        if (s?.product_id && q > 0) out[rid][s.product_id] = (out[rid][s.product_id] || 0) + q;
      }
    }
    return out;
  }, [slots]);

  // debounced autosave
  useEffect(() => {
    if (!loadedRef.current || !weekId) return;
    clearTimeout(timerRef.current);
    setSaving(true);
    timerRef.current = setTimeout(async () => {
      try { await api.put(`/inc/weeks/${weekId}`, { needs: derivedNeeds, slots, remainders, remainder_cartons: remainderCartons, stock_remainders: stockRem, stock_remainder_cartons: stockRemCarton, checks }); }
      catch (e) { toast.error(t("inc.saveFail")); }
      finally { setSaving(false); }
    }, 800);
    return () => clearTimeout(timerRef.current);
  }, [slots, remainders, remainderCartons, stockRem, stockRemCarton, checks, weekId]);

  const getSlot = (rid, k) => slots[rid]?.[k] || { product_id: "", qty: 0 };
  const setSlot = (rid, k, patch) => setSlots((s) => ({ ...s, [rid]: { ...(s[rid] || {}), [k]: { ...getSlot(rid, k), ...patch } } }));
  const getNeed = (rid, pid) => derivedNeeds[rid]?.[pid] || 0;
  const getRem = (uid, pid) => remainders[uid]?.[pid] || 0;
  const setRem = (uid, pid, val) => setRemainders((r) => ({ ...r, [uid]: { ...(r[uid] || {}), [pid]: val } }));
  const getRemCarton = (uid, pid) => remainderCartons[uid]?.[pid] || 0;
  const setRemCarton = (uid, pid, val) => setRemainderCartons((r) => ({ ...r, [uid]: { ...(r[uid] || {}), [pid]: val } }));
  const remTotal = (uid, pid, cartonSize) => getRem(uid, pid) + getRemCarton(uid, pid) * (cartonSize || 0);
  const getChk = (rid, k) => checks[rid]?.[k] || false;
  const toggleChk = (rid, k) => setChecks((c) => ({ ...c, [rid]: { ...(c[rid] || {}), [k]: !(c[rid]?.[k]) } }));

  const isIncontinent = (rid) => SLOT_DEFS.some((d) => { const v = slots[rid]?.[d.key]; return v?.product_id && Number(v.qty) > 0; });
  const unitIncontinents = (uid) => residentsOf(uid).filter((r) => isIncontinent(r.id)).length;
  const unitOccupied = (uid) => residentsOf(uid).filter((r) => r.name).length;
  const totalIncontinents = units.reduce((s, u) => s + unitIncontinents(u.id), 0);
  const totalOccupied = units.reduce((s, u) => s + unitOccupied(u.id), 0);
  const incPct = totalOccupied ? Math.round((totalIncontinents / totalOccupied) * 100) : 0;

  const unitTotal = (uid, pid) => residentsOf(uid).reduce((s, r) => s + getNeed(r.id, pid), 0);
  const unitNeedPkg = (uid, pid, size) => (size ? unitTotal(uid, pid) / size : 0);
  const unitOrderPkg = (uid, pid, size, cartonSize) => Math.max(0, unitNeedPkg(uid, pid, size) - remTotal(uid, pid, cartonSize));

  // Stock général (central) par produit
  const getStockRem = (pid) => stockRem[pid] || 0;
  const setStockRemVal = (pid, val) => setStockRem((r) => ({ ...r, [pid]: val }));
  const getStockRemCarton = (pid) => stockRemCarton[pid] || 0;
  const setStockRemCartonVal = (pid, val) => setStockRemCarton((r) => ({ ...r, [pid]: val }));

  // Agrégats produit (tous étages)
  const needPkgTotal = (pid, size) => units.reduce((s, u) => s + unitNeedPkg(u.id, pid, size), 0);
  const resteEtageEmb = (pid) => units.reduce((s, u) => s + getRem(u.id, pid), 0);
  const resteEtageCartons = (pid) => units.reduce((s, u) => s + getRemCarton(u.id, pid), 0);

  // Total à commander = besoin - reste étage (emb + cartons) - reste stock (emb + cartons)
  const globalPkg = (pid, size, cartonSize) => Math.max(0,
    needPkgTotal(pid, size)
    - resteEtageEmb(pid) - resteEtageCartons(pid) * (cartonSize || 0)
    - getStockRem(pid) - getStockRemCarton(pid) * (cartonSize || 0));
  const totalGlobalPkg = products.reduce((s, p) => s + globalPkg(p.id, p.package_size, p.carton_size), 0);
  const namedResidents = setup.residents.filter((r) => r.name && r.active !== false).length;
  const unitProducts = (uid) => products.filter((p) => unitTotal(uid, p.id) > 0 || getRem(uid, p.id) > 0 || getRemCarton(uid, p.id) > 0);
  const recapProducts = products.filter((p) => needPkgTotal(p.id, p.package_size) > 0 || resteEtageEmb(p.id) > 0 || resteEtageCartons(p.id) > 0 || getStockRem(p.id) > 0 || getStockRemCarton(p.id) > 0);

  const orderLines = useMemo(() => products.map((p) => {
    const packages = globalPkg(p.id, p.package_size, p.carton_size);
    const cartons = p.carton_size ? packages / p.carton_size : 0;
    const cost = cartons * (p.price_per_carton || 0);
    return { name: p.name, category: p.category, reference: p.reference || "", packages, carton_size: p.carton_size || 0, cartons, price_per_carton: p.price_per_carton || 0, cost };
  }).filter((l) => l.packages > 0), [products, derivedNeeds, remainders, remainderCartons, stockRem, stockRemCarton]);
  const totalCost = orderLines.reduce((s, l) => s + l.cost, 0);

  const [prodOpen, setProdOpen] = useState(false);

  const openNew = (copy) => {
    const today = new Date(); const mon = new Date(today); mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const s = mon.toISOString().slice(0, 10), e = sun.toISOString().slice(0, 10);
    setCopyMode(copy);
    setWf({ start_date: s, end_date: e, label: `Semaine du ${mon.toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit" })} au ${sun.toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}` });
    setNewOpen(true);
  };
  const createWeek = async () => {
    try {
      const url = copyMode ? `/inc/weeks/${weekId}/copy` : "/inc/weeks";
      const { data } = await api.post(url, wf);
      setNewOpen(false); toast.success(copyMode ? "Semaine copiée" : "Nouvelle semaine créée");
      await loadWeeks(); await loadWeek(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteWeek = async () => {
    if (weeks.length <= 1) return toast.error(t("inc.cantDeleteLast"));
    if (!window.confirm("Supprimer définitivement cette semaine ?")) return;
    try {
      await api.delete(`/inc/weeks/${weekId}`);
      toast.success(t("inc.weekDeleted"));
      const w = await loadWeeks();
      if (w[0]) await loadWeek(w[0].id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const active = units.find((u) => u.id === activeUnit) || units[0];

  const [fichesLoading, setFichesLoading] = useState(false);
  const generateFiches = async () => {
    const payloadUnits = units.map((u) => ({
      unit_label: u.label,
      unit_day: dayLabel(u.day_label),
      residents: residentsOf(u.id).filter((r) => isIncontinent(r.id) || autres.some((p) => checks[r.id]?.[p.id])).map((r) => {
        const day = [], night = [], others = [];
        SLOT_DEFS.forEach((d) => {
          const s = slots[r.id]?.[d.key];
          if (s?.product_id && Number(s.qty) > 0) {
            const p = setup.products.find((pp) => pp.id === s.product_id);
            const entry = { time: d.label, product: p?.name || "", icon_url: iconUrlFor(p?.icon), qty: Number(s.qty) };
            (d.cat === "JOUR" ? day : night).push(entry);
          }
        });
        autres.forEach((p) => {
          if (checks[r.id]?.[p.id]) others.push({ time: "", product: p.name, icon_url: iconUrlFor(p.icon), qty: 0 });
        });
        return { room: r.room || "", name: r.name || "", day, night, others };
      }),
    })).filter((u) => u.residents.length > 0);

    if (!payloadUnits.length) return toast.error(t("inc.fichesEmpty"));
    setFichesLoading(true);
    try {
      await downloadFichesPdf({ week_label: meta.label || "", maison: "", units: payloadUnits });
      toast.success(t("inc.fichesGenerated"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFichesLoading(false);
    }
  };

  const [unitOpen, setUnitOpen] = useState(false);
  const [editUnitId, setEditUnitId] = useState(null);
  const [uf, setUf] = useState({ label: "", day_label: "MARDI", display_order: 0 });
  const [resOpen, setResOpen] = useState(false);
  const [rf, setRf] = useState({ room: "", name: "", bed: "", name2: "", double: false });

  const openNewUnit = () => { setEditUnitId(null); setUf({ label: "", day_label: "MARDI", display_order: 0 }); setUnitOpen(true); };
  const openEditUnit = (u) => { setEditUnitId(u.id); setUf({ label: u.label, day_label: u.day_label, display_order: u.display_order || 0 }); setUnitOpen(true); };

  const saveUnit = async () => {
    if (!uf.label.trim()) return toast.error(t("inc.floorNameReq"));
    try {
      if (editUnitId) {
        await api.put(`/inc/units/${editUnitId}`, { ...uf });
        toast.success(t("inc.floorUpdated")); setUnitOpen(false); setEditUnitId(null);
        await loadSetup();
      } else {
        const { data } = await api.post("/inc/units", { ...uf, display_order: units.length + 1 });
        toast.success(t("inc.floorAdded")); setUnitOpen(false);
        await loadSetup(); setActiveUnit(data.id);
      }
      setUf({ label: "", day_label: "MARDI", display_order: 0 });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteUnit = async (u) => {
    if (!window.confirm(t("inc.confirmDeleteFloor", { name: u.label }))) return;
    try {
      await api.delete(`/inc/units/${u.id}`);
      toast.success(t("inc.floorDeleted"));
      const remaining = units.filter((x) => x.id !== u.id);
      await loadSetup();
      if (activeUnit === u.id) setActiveUnit(remaining[0]?.id || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addResident = async () => {
    if (!rf.room.trim()) return toast.error(t("inc.roomNumReq"));
    const base = residentsOf(active.id).length + 1;
    try {
      if (rf.double) {
        await api.post("/inc/residents", { room: `${rf.room.trim()} A`, name: rf.name || null, unit_id: active.id, display_order: base, active: true });
        await api.post("/inc/residents", { room: `${rf.room.trim()} B`, name: rf.name2 || null, unit_id: active.id, display_order: base + 1, active: true });
        toast.success(t("inc.doubleRoomAdded"));
      } else {
        const room = rf.bed ? `${rf.room.trim()} ${rf.bed}` : rf.room.trim();
        await api.post("/inc/residents", { room, name: rf.name || null, unit_id: active.id, display_order: base, active: true });
        toast.success(t("inc.roomAdded"));
      }
      setResOpen(false); setRf({ room: "", name: "", bed: "", name2: "", double: false });
      await loadSetup();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delResident = async (rid) => {
    try { await api.delete(`/inc/residents/${rid}`); await loadSetup(); }
    catch (e) { toast.error(t("inc.deleteFail")); }
  };

  return (
    <div className="p-5 sm:p-8 max-w-[1500px] mx-auto" data-testid="incontinence-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div data-tour="inc-header">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sky-300">{t("inc.tag")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl flex items-center justify-center bg-sky-500/20 text-sky-300"><ShieldPlus className="h-6 w-6" /></span>
            {t("inc.title")}
          </h1>
          <p className="text-muted-foreground mt-1">{t("inc.subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center no-print" data-tour="inc-actions">
          <Select value={weekId} onValueChange={loadWeek}>
            <SelectTrigger className="h-11 w-[240px]" data-testid="inc-week-select" data-tour="inc-week"><CalendarDays className="h-4 w-4 mr-1.5" /><SelectValue placeholder={t("inc.weekPh")} /></SelectTrigger>
            <SelectContent>{weeks.map((w) => <SelectItem key={w.id} value={w.id}>{w.label || `${w.start_date} → ${w.end_date}`}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" className="h-11" onClick={() => openNew(false)} data-testid="inc-new-week"><Plus className="h-4 w-4 mr-2" /> {t("inc.newWeek")}</Button>
          <Button variant="outline" className="h-11" onClick={() => openNew(true)} data-testid="inc-copy-week"><Copy className="h-4 w-4 mr-2" /> {t("inc.copyWeek")}</Button>
          <Button variant="outline" className="h-11" onClick={() => setProdOpen(true)} data-testid="inc-manage-products"><Package className="h-4 w-4 mr-2" /> {t("inc.products")}</Button>
          <Button className="h-11 bg-teal-600 hover:bg-teal-500 text-white" onClick={generateFiches} disabled={fichesLoading} data-testid="inc-generate-fiches" data-tour="inc-fiches">
            {fichesLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} {t("inc.generateFiches")}
          </Button>
          <Button variant="outline" className="h-11 text-red-400 hover:bg-red-500/10" onClick={deleteWeek} data-testid="inc-del-week" title={t("inc.deleteWeekTitle")}><Trash2 className="h-4 w-4" /></Button>
          <span className="text-xs text-muted-foreground flex items-center gap-1 w-[90px]">{saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("common.saving")}</> : <><Save className="h-3.5 w-3.5 text-emerald-400" /> {t("common.saved")}</>}</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mt-6" data-tour="inc-kpis">
        <Kpi icon={Boxes} label={t("inc.kpiPackages")} value={fmtNum(totalGlobalPkg)} sub={t("inc.kpiPackagesSub")} grad="linear-gradient(135deg,#0284c7,#0ea5e9)" delay={0.02} />
        <Kpi icon={Layers} label={t("inc.kpiFloors")} value={units.length} grad="linear-gradient(135deg,#7c3aed,#a855f7)" delay={0.06} />
        <Kpi icon={Users} label={t("inc.kpiResidents")} value={namedResidents} sub={`${setup.residents.length}`} grad="linear-gradient(135deg,#f59e0b,#f97316)" delay={0.1} />
        <Kpi icon={ShieldPlus} label={t("inc.kpiIncontinents")} value={totalIncontinents} sub={totalOccupied ? `${incPct}%` : "—"} grad="linear-gradient(135deg,#e11d48,#fb7185)" delay={0.12} />
        <Kpi icon={Package} label={t("inc.kpiActiveProducts")} value={products.length} sub={`${jour.length} · ${nuit.length} · ${autres.length}`} grad="linear-gradient(135deg,#059669,#10b981)" delay={0.14} />
      </div>

      {/* Unit tabs */}
      <div className="flex gap-2 flex-wrap mt-7 no-print" data-tour="inc-floors">
        {units.map((u) => (
          <button key={u.id} onClick={() => setActiveUnit(u.id)} data-testid={`inc-tab-${u.id}`}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition border ${activeUnit === u.id ? "bg-sky-500/20 border-sky-400/50 text-sky-200" : "glass border-transparent hover:bg-white/5"}`}>
            <span className="text-[10px] uppercase tracking-wider opacity-70 mr-1.5">{dayLabel(u.day_label)}</span>{u.label}
          </button>
        ))}
        <button onClick={openNewUnit} data-testid="inc-add-unit"
          className="px-4 py-2.5 rounded-xl text-sm font-semibold transition border border-dashed border-white/20 text-muted-foreground hover:text-white hover:border-sky-400/50">
          <Plus className="h-4 w-4 inline mr-1" /> {t("inc.addFloor")}
        </button>
      </div>

      {/* Table */}
      {active && (
        <div className="mt-4 glass rounded-2xl card-3d overflow-hidden" data-testid="inc-table" data-tour="inc-table">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display font-bold text-lg flex items-center gap-3">
              <span><span className="text-sky-300 text-sm uppercase tracking-wider mr-2">{dayLabel(active.day_label)}</span>{active.label}</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-200 border border-rose-400/30" data-testid={`inc-unit-incontinents-${active.id}`}>
                {unitIncontinents(active.id)} {t("inc.incontinents")}{unitOccupied(active.id) ? ` · ${Math.round((unitIncontinents(active.id) / unitOccupied(active.id)) * 100)}%` : ""}
              </span>
            </h2>
            <div className="flex items-center gap-2 no-print">
              <Button size="sm" variant="outline" className="h-8" onClick={() => setResOpen(true)} data-testid="inc-add-resident"><Plus className="h-3.5 w-3.5 mr-1" /> {t("inc.addRoom")}</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => openEditUnit(active)} data-testid="inc-edit-floor" title={t("inc.editFloor")}><Pencil className="h-3.5 w-3.5 mr-1" /> {t("inc.editFloor")}</Button>
              <Button size="sm" variant="outline" className="h-8 text-rose-300 border-rose-400/40 hover:bg-rose-500/10" onClick={() => deleteUnit(active)} data-testid="inc-delete-floor" title={t("inc.deleteFloor")}><Trash2 className="h-3.5 w-3.5 mr-1" /> {t("inc.deleteFloor")}</Button>
              <span className="text-xs text-muted-foreground">{meta.label}</span>
            </div>
          </div>

          {/* Légende */}
          <div className="px-5 py-2 text-[11px] text-muted-foreground border-b border-white/5 flex flex-wrap gap-x-5 gap-y-1">
            <span>{t("inc.legendSlots")}</span>
            <span>{t("inc.legendOthers")}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-[11px] uppercase">
                  <th rowSpan={2} className="sticky left-0 z-10 bg-[#161427] px-3 py-2 text-left">{t("inc.colCH")}</th>
                  <th rowSpan={2} className="sticky left-[52px] z-10 bg-[#161427] px-3 py-2 text-left min-w-[120px]">{t("inc.colName")}</th>
                  <th className="px-2 py-2 text-center bg-amber-500/15 text-amber-200" colSpan={3}><Sun className="h-3.5 w-3.5 inline mr-1" />{t("inc.day")}</th>
                  <th className="px-2 py-2 text-center bg-indigo-500/15 text-indigo-200"><Moon className="h-3.5 w-3.5 inline mr-1" />{t("inc.night")}</th>
                  {autres.length > 0 && <th className="px-2 py-2 text-center bg-teal-500/10 text-teal-200" colSpan={autres.length}>{t("inc.others")}</th>}
                </tr>
                <tr className="text-[10px] text-muted-foreground">
                  {SLOT_DEFS.map((d) => (
                    <th key={d.key} className={`px-2 py-1.5 font-semibold min-w-[140px] ${d.cat === "JOUR" ? "text-amber-200/80" : "text-indigo-200/80"}`}>{d.label}</th>
                  ))}
                  {autres.map((p) => (
                    <th key={p.id} className="px-2 py-1.5 text-center align-bottom" title={p.name}>
                      {p.icon && iconUrlFor(p.icon)
                        ? <img src={iconUrlFor(p.icon)} alt={p.name} className="h-7 w-7 object-contain mx-auto" />
                        : <span className="text-teal-200/80">{p.name.slice(0, 3)}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {residentsOf(active.id).map((r) => {
                  const blocked = r.incontinent === false;
                  return (
                  <tr key={r.id} className={`border-t border-white/5 hover:bg-white/[0.03] ${isIncontinent(r.id) ? "" : "opacity-80"}`} data-testid={`inc-row-${r.id}`}>
                    <td className="sticky left-0 z-10 bg-[#13111f] px-3 py-1.5 font-mono text-xs">{r.room}</td>
                    <td className="sticky left-[52px] z-10 bg-[#13111f] px-3 py-1.5 font-medium whitespace-nowrap group">
                      <span className="inline-flex items-center gap-1.5">
                        {isIncontinent(r.id) && <span className="h-1.5 w-1.5 rounded-full bg-rose-400" title={t("inc.titleIncontinent")} />}
                        {blocked && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/5 text-muted-foreground" title={t("inc.titleContinentMark")}>{t("inc.continent")}</span>}
                        {r.name || <span className="text-muted-foreground/40 italic">—</span>}
                        <button onClick={() => delResident(r.id)} data-testid={`del-res-${r.id}`} title={t("inc.delRoomTitle")}
                          className="no-print opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition"><Trash2 className="h-3.5 w-3.5" /></button>
                      </span>
                    </td>
                    {SLOT_DEFS.map((d) => {
                      const s = getSlot(r.id, d.key);
                      const list = d.cat === "JOUR" ? jour : nuit;
                      return (
                        <td key={d.key} className="px-1.5 py-1 align-top" onClick={blocked ? () => toast.error(t("inc.continentBlocked")) : undefined}>
                          <div className="flex items-center gap-1 min-w-[130px]">
                            <select value={s.product_id} disabled={blocked} data-testid={`slot-${r.id}-${d.key}-prod`}
                              onChange={(e) => setSlot(r.id, d.key, { product_id: e.target.value, qty: e.target.value ? (s.qty || 1) : 0 })}
                              className="flex-1 bg-white/5 rounded-md px-1.5 py-1 text-[11px] focus:bg-white/10 focus:outline-none border border-white/10 focus:border-sky-400/40 disabled:opacity-40 disabled:cursor-not-allowed">
                              <option value="">—</option>
                              {list.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <input type="number" min="0" value={s.qty || ""} disabled={blocked || !s.product_id} data-testid={`slot-${r.id}-${d.key}-qty`}
                              onChange={(e) => setSlot(r.id, d.key, { qty: e.target.value === "" ? 0 : Number(e.target.value) })}
                              className="w-11 bg-transparent rounded-md px-1 py-1 text-center text-xs focus:bg-white/10 focus:outline-none border border-transparent focus:border-sky-400/40 disabled:opacity-30" />
                          </div>
                        </td>
                      );
                    })}
                    {autres.map((p) => (
                      <td key={p.id} className="px-1 py-1 text-center">
                        <button onClick={() => toggleChk(r.id, p.id)} data-testid={`chk-${r.id}-${p.id}`} title={p.name}
                          className={`h-6 w-6 rounded-md text-xs font-bold transition ${getChk(r.id, p.id) ? "bg-teal-500 text-white" : "bg-white/5 text-transparent hover:bg-white/10"}`}>X</button>
                      </td>
                    ))}
                  </tr>
                  );
                })}
                {!residentsOf(active.id).length && <tr><td colSpan={6 + autres.length} className="px-3 py-6 text-center text-muted-foreground text-sm">{t("inc.noRoom")}</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Récap produit de l'étage (besoins dérivés des créneaux) */}
          <div className="px-5 py-4 border-t border-white/10">
            <h3 className="text-[11px] uppercase tracking-wider text-sky-200 font-semibold mb-2">{t("inc.recapUnitTitle")} — {active.label}</h3>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-[#161427] text-[11px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">{t("inc.colProduct")}</th>
                    <th className="px-2 py-2 text-center">{t("inc.colNeedPkg")}</th>
                    <th className="px-2 py-2 text-center text-amber-200">{t("inc.colResteFloorPkg")}</th>
                    <th className="px-2 py-2 text-center text-amber-200">{t("inc.colResteFloorCarton")}</th>
                    <th className="px-2 py-2 text-center text-sky-200">{t("inc.colToOrderPkg")}</th>
                  </tr>
                </thead>
                <tbody>
                  {unitProducts(active.id).map((p) => (
                    <tr key={p.id} className="border-t border-white/5" data-testid={`inc-unit-prod-${p.id}`}>
                      <td className="px-3 py-1.5"><span className={`inline-block h-2 w-2 rounded-full mr-2 ${p.category === "JOUR" ? "bg-amber-400" : "bg-indigo-400"}`} />{p.name} <span className="text-muted-foreground text-xs">/{p.package_size}{p.carton_size ? ` · ${p.carton_size}/carton` : ""}</span></td>
                      <td className="px-2 py-1.5 text-center font-semibold">{fmtNum(unitNeedPkg(active.id, p.id, p.package_size))}</td>
                      <td className="px-2 py-1.5 text-center">
                        <input type="number" min="0" value={getRem(active.id, p.id) || ""} data-testid={`rem-${p.id}`}
                          onChange={(e) => setRem(active.id, p.id, e.target.value === "" ? 0 : Number(e.target.value))}
                          className="w-16 bg-amber-500/5 rounded-md px-1 py-1 text-center text-xs focus:bg-amber-500/15 focus:outline-none border border-amber-400/20" />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <input type="number" min="0" value={getRemCarton(active.id, p.id) || ""} disabled={!p.carton_size} title={!p.carton_size ? "Définir paquets/carton dans Gestion des produits" : ""} data-testid={`remcarton-${p.id}`}
                          onChange={(e) => setRemCarton(active.id, p.id, e.target.value === "" ? 0 : Number(e.target.value))}
                          className="w-16 bg-amber-500/5 rounded-md px-1 py-1 text-center text-xs focus:bg-amber-500/15 focus:outline-none border border-amber-400/20 disabled:opacity-30" />
                      </td>
                      <td className="px-2 py-1.5 text-center font-bold text-sky-200">{fmtNum(unitOrderPkg(active.id, p.id, p.package_size, p.carton_size))}</td>
                    </tr>
                  ))}
                  {!unitProducts(active.id).length && <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground text-sm">{t("inc.noNeedUnit")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Récapitulatif général */}
      <div className="mt-7 glass rounded-2xl card-3d p-5" data-testid="inc-recap" data-tour="inc-recap">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><Boxes className="h-5 w-5 text-sky-300" /> {t("inc.generalRecap")}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t("inc.recapFormula")}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-[11px] uppercase text-muted-foreground">
                <th className="px-3 py-2 text-left">{t("inc.colProduct")}</th>
                <th className="px-2 py-2 text-center">{t("inc.colNeed")}</th>
                <th className="px-2 py-2 text-center text-amber-200">{t("inc.colResteFloorPkg")}</th>
                <th className="px-2 py-2 text-center text-amber-200">{t("inc.colResteFloorCarton")}</th>
                <th className="px-2 py-2 text-center text-fuchsia-200">{t("inc.colStockCarton")}</th>
                <th className="px-2 py-2 text-center text-fuchsia-200">{t("inc.colStockPkg")}</th>
                <th className="px-3 py-2 text-right text-sky-200">{t("inc.colToOrder")}</th>
              </tr>
            </thead>
            <tbody>
              {recapProducts.map((p) => (
                <tr key={p.id} className="border-t border-white/5" data-testid={`inc-recap-row-${p.id}`}>
                  <td className="px-3 py-1.5"><span className={`inline-block h-2 w-2 rounded-full mr-2 ${p.category === "JOUR" ? "bg-amber-400" : "bg-indigo-400"}`} />{p.name} <span className="text-muted-foreground text-xs">/{p.package_size}{p.carton_size ? ` · ${p.carton_size}/carton` : ""}</span></td>
                  <td className="px-2 py-1.5 text-center font-semibold">{fmtNum(needPkgTotal(p.id, p.package_size))}</td>
                  <td className="px-2 py-1.5 text-center text-amber-200/90">{fmtNum(resteEtageEmb(p.id))}</td>
                  <td className="px-2 py-1.5 text-center text-amber-200/90">{fmtNum(resteEtageCartons(p.id))}</td>
                  <td className="px-2 py-1.5 text-center">
                    <input type="number" min="0" value={getStockRemCarton(p.id) || ""} disabled={!p.carton_size} title={!p.carton_size ? "Définir paquets/carton dans Gestion des produits" : ""} data-testid={`stock-carton-${p.id}`}
                      onChange={(e) => setStockRemCartonVal(p.id, e.target.value === "" ? 0 : Number(e.target.value))}
                      className="w-16 bg-fuchsia-500/5 rounded-md px-1 py-1 text-center text-xs focus:bg-fuchsia-500/15 focus:outline-none border border-fuchsia-400/20 disabled:opacity-30" />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <input type="number" min="0" value={getStockRem(p.id) || ""} data-testid={`stock-emb-${p.id}`}
                      onChange={(e) => setStockRemVal(p.id, e.target.value === "" ? 0 : Number(e.target.value))}
                      className="w-16 bg-fuchsia-500/5 rounded-md px-1 py-1 text-center text-xs focus:bg-fuchsia-500/15 focus:outline-none border border-fuchsia-400/20" />
                  </td>
                  <td className="px-3 py-1.5 text-right font-bold text-sky-200">{fmtNum(globalPkg(p.id, p.package_size, p.carton_size))}</td>
                </tr>
              ))}
              {!recapProducts.length && <tr><td colSpan={7} className="px-3 py-4 text-center text-muted-foreground text-sm">{t("inc.noNeed")}</td></tr>}
              <tr className="border-t-2 border-sky-400/30 bg-sky-500/10 font-bold">
                <td className="px-3 py-2 uppercase text-[11px]" colSpan={6}>{t("inc.grandTotal")}</td>
                <td className="px-3 py-2 text-right text-sky-200 text-base">{fmtNum(totalGlobalPkg)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Budget, bon de commande & statistiques */}
      <StatsBudget orderLines={orderLines} totalCost={totalCost} weekLabel={meta.label} />

      {/* New / Copy week dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>        <DialogContent>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarDays className="h-5 w-5 text-sky-400" /> {copyMode ? t("inc.copyWeekTitle") : t("inc.newWeekTitle")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{t("inc.weekDesc")}</DialogDescription>
          <div className="space-y-3 py-2">
            {copyMode && <p className="text-sm text-muted-foreground">{t("inc.copyHint")}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("inc.from")}</Label><Input type="date" value={wf.start_date} onChange={(e) => setWf({ ...wf, start_date: e.target.value })} data-testid="inc-wf-start" /></div>
              <div className="space-y-2"><Label>{t("inc.to")}</Label><Input type="date" value={wf.end_date} onChange={(e) => setWf({ ...wf, end_date: e.target.value })} data-testid="inc-wf-end" /></div>
            </div>
            <div className="space-y-2"><Label>{t("inc.label")}</Label><Input value={wf.label} onChange={(e) => setWf({ ...wf, label: e.target.value })} data-testid="inc-wf-label" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bg-sky-600 hover:bg-sky-500" onClick={createWeek} data-testid="inc-wf-save">{copyMode ? t("inc.copyBtn") : t("inc.createBtn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add unit dialog */}
      <Dialog open={unitOpen} onOpenChange={setUnitOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Layers className="h-5 w-5 text-sky-400" /> {editUnitId ? t("inc.editFloorTitle") : t("inc.addUnitTitle")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{t("inc.unitDesc")}</DialogDescription>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>{t("inc.floorName")}</Label><Input value={uf.label} onChange={(e) => setUf({ ...uf, label: e.target.value })} placeholder={t("inc.floorNamePh")} data-testid="uf-label" /></div>
            <div className="space-y-2"><Label>{t("inc.distribDay")}</Label>
              <Select value={uf.day_label} onValueChange={(v) => setUf({ ...uf, day_label: v })}>
                <SelectTrigger data-testid="uf-day"><SelectValue /></SelectTrigger>
                <SelectContent>{["LUNDI", "MARDI", "MERCREDI", "JEUDI", "VENDREDI", "SAMEDI", "DIMANCHE"].map((d) => <SelectItem key={d} value={d}>{dayLabel(d)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setUnitOpen(false); setEditUnitId(null); }}>{t("common.cancel")}</Button><Button className="bg-sky-600 hover:bg-sky-500" onClick={saveUnit} data-testid="uf-save">{editUnitId ? t("inc.save") : t("inc.add")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add resident dialog */}
      <Dialog open={resOpen} onOpenChange={setResOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Users className="h-5 w-5 text-sky-400" /> {t("inc.addRoomTitle")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{t("inc.roomDesc")}</DialogDescription>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">{t("inc.floorLabel")} <b className="text-foreground">{active?.label}</b>. {t("inc.emptyNameHint")}</p>
            <div className="flex items-center justify-between glass rounded-xl px-3 py-2.5">
              <div className="flex items-center gap-2 text-sm"><Users className="h-4 w-4 text-sky-300" /> {t("inc.doubleRoom")}</div>
              <Switch checked={rf.double} onCheckedChange={(v) => setRf({ ...rf, double: v })} data-testid="rf-double" />
            </div>
            {rf.double ? (
              <>
                <div className="space-y-2"><Label>{t("inc.roomNumber")}</Label><Input value={rf.room} onChange={(e) => setRf({ ...rf, room: e.target.value })} placeholder={t("inc.roomNumberPh")} data-testid="rf-room" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>{t("inc.nameA")}</Label><Input value={rf.name} onChange={(e) => setRf({ ...rf, name: e.target.value })} placeholder={t("inc.namePh")} data-testid="rf-name" /></div>
                  <div className="space-y-2"><Label>{t("inc.nameB")}</Label><Input value={rf.name2} onChange={(e) => setRf({ ...rf, name2: e.target.value })} placeholder={t("inc.namePh2")} data-testid="rf-name2" /></div>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>{t("inc.room")}</Label><Input value={rf.room} onChange={(e) => setRf({ ...rf, room: e.target.value })} placeholder={t("inc.roomPh")} data-testid="rf-room" /></div>
                <div className="space-y-2"><Label>{t("inc.bed")}</Label>
                  <Select value={rf.bed || "none"} onValueChange={(v) => setRf({ ...rf, bed: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="rf-bed"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>{t("inc.nameOpt")}</Label><Input value={rf.name} onChange={(e) => setRf({ ...rf, name: e.target.value })} placeholder={t("inc.namePh")} data-testid="rf-name" /></div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setResOpen(false)}>{t("common.cancel")}</Button><Button className="bg-sky-600 hover:bg-sky-500" onClick={addResident} data-testid="rf-save">{t("inc.add")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductsDialog open={prodOpen} onOpenChange={setProdOpen} products={setup.products} onReload={loadSetup} />
    </div>
  );
}
